import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Pencil, Eraser, Users, Package, MapPin, Trash2,
  Undo2, Redo2, Sparkles, Loader2, X, Plus, ArrowLeft
} from "lucide-react";

// Tag types with colors
const TAG_TYPES = {
  character: { label: 'Character', icon: Users, color: '#d4a84b' },
  prop: { label: 'Prop', icon: Package, color: '#14b8a6' },
  location: { label: 'Location', icon: MapPin, color: '#f43f5e' }
};

export default function EmbeddedQuickDraw({
  onBack,
  characters = [],
  locations = [],
  onGenerate,
  isGenerating = false,
  segmentText = "",
  segmentNumber = null,
}) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [tool, setTool] = useState('pencil');
  const [isDrawing, setIsDrawing] = useState(false);
  const [brushSize, setBrushSize] = useState(4);
  const [tags, setTags] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [showTagMenu, setShowTagMenu] = useState(false);
  const [tagPosition, setTagPosition] = useState({ x: 0, y: 0 });
  const [selectedTagType, setSelectedTagType] = useState(null);
  const [tagSearch, setTagSearch] = useState('');
  const [canvasReady, setCanvasReady] = useState(false);

  // Resize canvas to fit container, preserving drawing
  const resizeCanvas = useCallback((isInit = false) => {
    if (!canvasRef.current || !containerRef.current) return;
    const canvas = canvasRef.current;
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    if (rect.width < 10 || rect.height < 10) return;

    const dpr = window.devicePixelRatio || 1;
    const ctx = canvas.getContext('2d');

    if (isInit) {
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      ctx.scale(dpr, dpr);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, rect.width, rect.height);
      setCanvasReady(true);
      return;
    }

    // Preserve current drawing
    const imageData = canvas.toDataURL();
    const prevW = parseFloat(canvas.style.width) || rect.width;
    const prevH = parseFloat(canvas.style.height) || rect.height;

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);

    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, rect.width, rect.height);
      ctx.drawImage(img, 0, 0, prevW, prevH, 0, 0, rect.width, rect.height);
    };
    img.src = imageData;
  }, []);

  // Initialize canvas + observe container resize (handles panel collapse/expand)
  useEffect(() => {
    const timer = setTimeout(() => resizeCanvas(true), 50);

    const observer = new ResizeObserver(() => {
      if (canvasReady) resizeCanvas(false);
    });
    if (containerRef.current) observer.observe(containerRef.current);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [canvasReady, resizeCanvas]);

  const saveToHistory = useCallback(() => {
    if (!canvasRef.current) return;
    const imageData = canvasRef.current.toDataURL();
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push({ imageData, tags: [...tags] });
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex, tags]);

  const undo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      restoreFromHistory(newIndex);
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      restoreFromHistory(newIndex);
    }
  };

  const restoreFromHistory = (index) => {
    const state = history[index];
    if (state && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const rect = containerRef.current?.getBoundingClientRect();
      
      const img = new Image();
      img.onload = () => {
        const dpr = window.devicePixelRatio || 1;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.scale(dpr, dpr);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, rect?.width || canvas.width, rect?.height || canvas.height);
        ctx.drawImage(img, 0, 0, rect?.width || canvas.width, rect?.height || canvas.height);
      };
      img.src = state.imageData;
      setTags(state.tags);
    }
  };

  const clearCanvas = () => {
    if (canvasRef.current && containerRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const rect = containerRef.current.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, rect.width, rect.height);
      setTags([]);
      saveToHistory();
    }
  };

  const getCanvasCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    if (tool === 'tag') {
      const rect = canvasRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      setTagPosition({ x: (x / rect.width) * 100, y: (y / rect.height) * 100 });
      setShowTagMenu(true);
      return;
    }

    setIsDrawing(true);
    const ctx = canvasRef.current.getContext('2d');
    const coords = getCanvasCoordinates(e);
    
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = brushSize;
    ctx.strokeStyle = tool === 'eraser' ? '#ffffff' : '#000000';
  };

  const draw = (e) => {
    if (!isDrawing || tool === 'tag') return;
    
    const ctx = canvasRef.current.getContext('2d');
    const coords = getCanvasCoordinates(e);
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      saveToHistory();
    }
  };

  const addTag = (type, name) => {
    const newTag = {
      id: Date.now(),
      type,
      name,
      position: tagPosition
    };
    setTags([...tags, newTag]);
    setShowTagMenu(false);
    setSelectedTagType(null);
    setTagSearch('');
  };

  const removeTag = (tagId) => {
    setTags(tags.filter(t => t.id !== tagId));
  };

  const handleGenerate = () => {
    if (!canvasRef.current) return;
    const sketchData = canvasRef.current.toDataURL('image/png');
    onGenerate({
      sketch_data: sketchData,
      tags: tags.map(t => ({ type: t.type, name: t.name, position: t.position })),
      segment_text: segmentText,
      segment_number: segmentNumber,
    });
  };

  const getFilteredItems = () => {
    if (!selectedTagType) return [];
    const search = tagSearch.toLowerCase();
    
    if (selectedTagType === 'character') {
      return characters.filter(c => c.name.toLowerCase().includes(search));
    } else if (selectedTagType === 'location') {
      return locations.filter(l => l.name.toLowerCase().includes(search));
    }
    return [];
  };

  return (
    <div className="h-full flex flex-col bg-[var(--bg-surface)]">
      {/* Header */}
      <div className="p-4 border-b border-[var(--border-subtle)] flex items-center gap-3">
        <Button onClick={onBack} variant="ghost" size="sm" className="text-[var(--text-muted)]">
          <ArrowLeft size={16} />
        </Button>
        <h3 className="font-semibold text-[var(--text-primary)]">Quick Draw</h3>
        <span className="text-xs text-[var(--text-muted)]">
          {segmentNumber ? `Segment ${segmentNumber}` : 'Sketch → AI Shot'}
        </span>
      </div>

      {/* Segment context banner */}
      {segmentText && (
        <div className="px-3 py-2 bg-[var(--bg-overlay)] border-b border-[var(--border-subtle)]">
          <p className="text-[10px] text-[var(--text-muted)] line-clamp-2">{segmentText.slice(0, 120)}</p>
        </div>
      )}

      {/* Toolbar */}
      <div className="p-3 border-b border-[var(--border-subtle)] flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 bg-[var(--bg-elevated)] rounded-lg p-1">
          <button
            onClick={() => setTool('pencil')}
            className={`p-2 rounded-md transition-colors ${
              tool === 'pencil' 
                ? 'bg-[var(--color-gold-500)]/20 text-[var(--color-gold-400)]' 
                : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
            }`}
          >
            <Pencil size={18} />
          </button>
          <button
            onClick={() => setTool('eraser')}
            className={`p-2 rounded-md transition-colors ${
              tool === 'eraser' 
                ? 'bg-[var(--color-gold-500)]/20 text-[var(--color-gold-400)]' 
                : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
            }`}
          >
            <Eraser size={18} />
          </button>
          <button
            onClick={() => setTool('tag')}
            className={`p-2 rounded-md transition-colors ${
              tool === 'tag' 
                ? 'bg-[var(--color-gold-500)]/20 text-[var(--color-gold-400)]' 
                : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
            }`}
          >
            <Plus size={18} />
          </button>
        </div>

        <div className="h-6 w-px bg-[var(--border-subtle)]" />

        <button onClick={undo} disabled={historyIndex <= 0} className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] disabled:opacity-30">
          <Undo2 size={18} />
        </button>
        <button onClick={redo} disabled={historyIndex >= history.length - 1} className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] disabled:opacity-30">
          <Redo2 size={18} />
        </button>
        <button onClick={clearCanvas} className="p-2 text-red-400 hover:text-red-300">
          <Trash2 size={18} />
        </button>

        <div className="h-6 w-px bg-[var(--border-subtle)]" />

        <div className="flex items-center gap-2">
          <span className="text-xs text-[var(--text-muted)]">Size</span>
          <input
            type="range"
            min="1"
            max="20"
            value={brushSize}
            onChange={(e) => setBrushSize(parseInt(e.target.value))}
            className="w-20 accent-[var(--color-gold-500)]"
          />
        </div>
      </div>

      {/* Canvas Container - Takes all available space */}
      <div 
        ref={containerRef}
        className="flex-1 relative bg-[#1a1918]"
        style={{ minHeight: 0 }}
      >
        <canvas
          ref={canvasRef}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          className="absolute inset-0 cursor-crosshair"
          style={{ backgroundColor: '#ffffff' }}
        />

        {/* Tags overlay */}
        {tags.map(tag => {
          const config = TAG_TYPES[tag.type];
          const TagIcon = config?.icon || Package;
          return (
            <div
              key={tag.id}
              className="absolute flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium shadow-lg cursor-pointer group"
              style={{
                left: `${tag.position.x}%`,
                top: `${tag.position.y}%`,
                transform: 'translate(-50%, -50%)',
                backgroundColor: config?.color || '#666',
                color: '#000'
              }}
            >
              <TagIcon size={12} />
              <span>{tag.name}</span>
              <button
                onClick={() => removeTag(tag.id)}
                className="opacity-0 group-hover:opacity-100 ml-1 hover:text-white"
              >
                <X size={12} />
              </button>
            </div>
          );
        })}

        {/* Tag selection menu */}
        {showTagMenu && (
          <div
            className="absolute z-50 bg-[var(--bg-elevated)] border border-[var(--border-default)] rounded-xl shadow-2xl p-3 w-56"
            style={{
              left: `${Math.min(tagPosition.x, 70)}%`,
              top: `${Math.min(tagPosition.y, 70)}%`
            }}
          >
            {!selectedTagType ? (
              <>
                <p className="text-xs text-[var(--text-muted)] mb-2">What's here?</p>
                {Object.entries(TAG_TYPES).map(([key, { label, icon: Icon, color }]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedTagType(key)}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[var(--bg-hover)]"
                  >
                    <Icon size={16} style={{ color }} />
                    <span className="text-sm text-[var(--text-primary)]">{label}</span>
                  </button>
                ))}
                <button
                  onClick={() => setShowTagMenu(false)}
                  className="w-full mt-2 text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button onClick={() => setSelectedTagType(null)} className="text-xs text-[var(--text-muted)] mb-2 flex items-center gap-1">
                  <ArrowLeft size={12} /> Back
                </button>
                <Input
                  value={tagSearch}
                  onChange={(e) => setTagSearch(e.target.value)}
                  placeholder={`Search ${selectedTagType}...`}
                  className="mb-2 text-sm"
                  autoFocus
                />
                <div className="max-h-32 overflow-y-auto">
                  {getFilteredItems().map((item, idx) => (
                    <button
                      key={idx}
                      onClick={() => addTag(selectedTagType, item.name)}
                      className="w-full text-left px-2 py-1.5 rounded hover:bg-[var(--bg-hover)] text-sm text-[var(--text-primary)]"
                    >
                      {item.name}
                    </button>
                  ))}
                  {selectedTagType === 'prop' && tagSearch && (
                    <button
                      onClick={() => addTag('prop', tagSearch)}
                      className="w-full text-left px-2 py-1.5 rounded hover:bg-[var(--bg-hover)] text-sm text-[var(--color-teal-400)]"
                    >
                      + Add "{tagSearch}"
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-[var(--border-subtle)] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs text-[var(--text-muted)]">{tags.length} tags</span>
          {tags.slice(0, 3).map(tag => (
            <span
              key={tag.id}
              className="px-2 py-0.5 rounded-full text-[10px] font-medium"
              style={{ 
                backgroundColor: TAG_TYPES[tag.type]?.color + '30',
                color: TAG_TYPES[tag.type]?.color
              }}
            >
              {tag.name}
            </span>
          ))}
          {tags.length > 3 && <span className="text-[10px] text-[var(--text-muted)]">+{tags.length - 3}</span>}
        </div>
        
        <Button
          onClick={handleGenerate}
          disabled={tags.length === 0 || isGenerating}
          className="btn-primary"
        >
          {isGenerating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          <span className="ml-2">Generate Shot</span>
        </Button>
      </div>
    </div>
  );
}
